import React, { useState, useMemo, useCallback } from "react";
import { Alert, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { formatDateISO } from "../../../utils/utils";
import { fetchTransfersConversations } from "../../../services/dashboardModule/dashboardServices.js";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PURPLE = "#7F77DD";
const GREEN  = "#1D9E75";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";

const LANGUAGE_COLORS = {
    English: BLUE,
    Spanish: PURPLE,
};

// Parse the various LastConvDate shapes the API may emit into a Date.
const parseConvDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
    if (typeof v !== "string") return null;
    let s = v.trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [yy, mm, dd] = s.split("-").map(Number);
        return new Date(yy, mm - 1, dd, 0, 0, 0, 0);
    }
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) s = s.replace(" ", "T");
    if (s.includes("T") && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = `${s}Z`;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

// ── Column config ──────────────────────────────────────────────────
const COLS = [
    { key: "LastConvDate",         label: "Conv. Date",           width: 120, align: "left"  },
    { key: "EnglishTotal",         label: "English",              width: 80,  align: "right" },
    { key: "EnglishTransferRate",  label: "Eng. Transfer %",      width: 130, align: "right" },
    { key: "EnglishNoTransfer",    label: "Eng. No Transfer %",   width: 140, align: "right" },
    { key: "Spanish",              label: "Spanish",              width: 80,  align: "right" },
    { key: "SpanishTransferRate",  label: "Spa. Transfer %",      width: 130, align: "right" },
    { key: "SpanishNoTransfer",    label: "Spa. No Transfer %",   width: 140, align: "right" },
    { key: "TotalRecords",         label: "Total",                width: 80,  align: "right" },
];

// Language buckets — used to figure out which language a clicked cell belongs to.
const ENG_COLS     = new Set(["EnglishTotal", "EnglishTransferRate", "EnglishNoTransfer"]);
const SPA_COLS     = new Set(["Spanish", "SpanishTransferRate", "SpanishNoTransfer"]);
const RATE_COLS    = new Set(["EnglishTransferRate", "SpanishTransferRate"]);
const NO_RATE_COLS = new Set(["EnglishNoTransfer", "SpanishNoTransfer"]);

// 40px drill icon column at the start
const TABLE_COLS  = [{ key: "_drill", label: "", width: 40, align: "left" }, ...COLS];
const GRID_COLS   = TABLE_COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH = TABLE_COLS.reduce((s, c) => s + c.width, 0) + 64;
const ROWS_PER_PAGE = 25;

// ── Rate pill ──────────────────────────────────────────────────────
function RatePill({ value, isTransfer, onClick, clickable }) {
    const num = parseFloat(value) || 0;
    if (!value && value !== 0) return <Box sx={{ color: "text.disabled" }}>—</Box>;
    const color = isTransfer
        ? (num >= 50 ? GREEN : num >= 20 ? BLUE : AMBER)
        : PINK;
    return (
        <Box
            component="span"
            onClick={clickable ? onClick : undefined}
            sx={{
                display: "inline-block",
                px: "7px", py: "1px", borderRadius: "20px",
                fontSize: 11, fontWeight: 500,
                bgcolor: `${color}18`,
                color,
                border: `0.5px solid ${color}44`,
                cursor: clickable ? "pointer" : "default",
                "&:hover": clickable ? { bgcolor: `${color}28` } : {},
            }}
        >
            {value}
        </Box>
    );
}

// ── TransferTable ──────────────────────────────────────────────────
const TransferTable = ({ rows = [], loadingData = false, error = "", channel = "Texts" }) => {
    const [page,    setPage]    = useState(0);
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState("asc");
    const [drill,   setDrill]   = useState(null);

    const handleSortCol = (key) => {
        if (key === "_drill") return;
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
        setPage(0);
    };

    const sortedData = useMemo(() => {
        if (!sortKey) return rows;
        return [...rows].sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            const an = Number(av), bn = Number(bv);
            const cmp = !isNaN(an) && !isNaN(bn)
                ? an - bn
                : String(av ?? "").localeCompare(String(bv ?? ""));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [rows, sortKey, sortDir]);

    const pagedData  = useMemo(() =>
        sortedData.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
    , [sortedData, page]);

    const totalPages = Math.ceil(rows.length / ROWS_PER_PAGE);

    const kpis = useMemo(() => {
        const sum = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
        const avgRate = (k) => rows.length
            ? rows.reduce((a, r) => a + (parseFloat(r[k]) || 0), 0) / rows.length
            : 0;
        return {
            total:   sum("TotalRecords"),
            english: sum("EnglishTotal"),
            spanish: sum("Spanish"),
            engRate: avgRate("EnglishTransferRate").toFixed(1),
            spaRate: avgRate("SpanishTransferRate").toFixed(1),
        };
    }, [rows]);

    // Date range covered by the currently-loaded rows. Used as the drill
    // window for language-only KPI clicks (no specific date).
    // Derived from rows rather than parent state so this component stays
    // self-contained — parent does its own client-side filtering.
    const dateRange = useMemo(() => {
        if (!rows.length) return { start: null, end: null };
        let min = null, max = null;
        rows.forEach(r => {
            const d = parseConvDate(r.LastConvDate);
            if (!d) return;
            if (!min || d < min) min = d;
            if (!max || d > max) max = d;
        });
        return { start: min, end: max };
    }, [rows]);

    // ── Drill-down ────────────────────────────────────────────────
    // language + dateRaw → that language on that date
    // dateRaw only       → all transfers on that date
    // language only      → that language across the loaded date range
    const openDrill = useCallback(({ language = null, dateRaw = null } = {}) => {
        const langColor = language ? LANGUAGE_COLORS[language] : null;
        const dateObj   = dateRaw ? parseConvDate(dateRaw) : null;

        const title =
            language ? `Transfers: ${language}` :
            dateObj  ? `Transfers on ${formatDateISO(dateRaw)}` :
                       "Transfer conversations";

        // For per-date drill, send the same instant as start and end.
        // For language-only drill, fall back to the loaded date range.
        // (SP needs to treat same-day instants as a full day window.)
        const dStart = dateObj
            ? dateObj.toISOString()
            : (dateRange.start ? dateRange.start.toISOString() : null);
        const dEnd   = dateObj
            ? dateObj.toISOString()
            : (dateRange.end   ? dateRange.end.toISOString()   : null);

        setDrill({
            title,
            metric: language ? { label: "Language", value: language, color: langColor } : null,
            context: [
                dateObj && { label: "Date", value: formatDateISO(dateRaw) },
                !dateObj && dateRange.start && { label: "From", value: formatDateISO(dateRange.start) },
                !dateObj && dateRange.end   && { label: "To",   value: formatDateISO(dateRange.end)   },
            ].filter(Boolean),
            channel,
            emotionColors: LANGUAGE_COLORS,
            fetchData: () => fetchTransfersConversations({
                language,
                startDate:   dStart,
                endDate:     dEnd,
                channeltype: channel,
            }),
        });
    }, [channel, dateRange]);

    const gridSx = {
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        width: `${TABLE_WIDTH}px`,
        px: 2,
    };

    if (loadingData) {
        return (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
                <CircularProgress size={20} /> Loading…
            </Box>
        );
    }

    if (error) return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;

    if (rows.length === 0) {
        return (
            <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
                No records found.
            </Typography>
        );
    }

    return (
        <>
            {/* ── KPIs — language cards drill the language across the range ── */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {[
                    {
                        label: "Total Records", value: kpis.total.toLocaleString(), color: BLUE,
                        onClick: undefined,
                    },
                    {
                        label: "English", value: kpis.english.toLocaleString(), color: BLUE,
                        onClick: kpis.english > 0 ? () => openDrill({ language: "English" }) : undefined,
                    },
                    {
                        label: "Avg Eng. Transfer %", value: `${kpis.engRate}%`, color: GREEN,
                        onClick: kpis.english > 0 ? () => openDrill({ language: "English" }) : undefined,
                    },
                    {
                        label: "Spanish", value: kpis.spanish.toLocaleString(), color: BLUE,
                        onClick: kpis.spanish > 0 ? () => openDrill({ language: "Spanish" }) : undefined,
                    },
                    {
                        label: "Avg Spa. Transfer %", value: `${kpis.spaRate}%`, color: GREEN,
                        onClick: kpis.spanish > 0 ? () => openDrill({ language: "Spanish" }) : undefined,
                    },
                ].map(k => (
                    <Box key={k.label} onClick={k.onClick} sx={{
                        background: "#f7f7f7", borderRadius: 2,
                        p: "10px 14px", flex: 1, minWidth: 0,
                        cursor: k.onClick ? "pointer" : "default",
                        transition: "background .15s",
                        "&:hover": k.onClick ? { background: "#efefef" } : {},
                    }}>
                        <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
                            {k.label}
                        </Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 500, color: k.color, lineHeight: 1.2 }}>
                            {k.value}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* ── Table ── */}
            <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto" }}>
                {/* Header */}
                <Box sx={{
                    ...gridSx, py: 1,
                    background: "#f7f7f7",
                    borderBottom: "0.5px solid", borderColor: "divider",
                    fontSize: 11, fontWeight: 500,
                    textTransform: "uppercase", letterSpacing: "0.05em",
                    alignItems: "center",
                }}>
                    {TABLE_COLS.map(col => (
                        <Box
                            key={col.key}
                            onClick={() => handleSortCol(col.key)}
                            sx={{
                                textAlign: col.align,
                                cursor: col.key === "_drill" ? "default" : "pointer",
                                userSelect: "none",
                                display: "flex", gap: 0.5, alignItems: "center",
                                justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                color: sortKey === col.key ? BLUE : "text.secondary",
                                "&:hover": col.key === "_drill" ? {} : { color: BLUE },
                            }}
                        >
                            {col.label}
                            {sortKey === col.key && (
                                <span style={{ fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Rows */}
                {pagedData.map((row, i) => {
                    const engCount = Number(row.EnglishTotal) || 0;
                    const spaCount = Number(row.Spanish)      || 0;

                    return (
                        <Box key={row.id ?? i} sx={{
                            ...gridSx, py: "7px", fontSize: 12,
                            borderBottom: "0.5px solid", borderColor: "divider",
                            background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                            "&:hover": { background: "rgba(55,138,221,0.06)" },
                            alignItems: "center",
                        }}>
                            {/* drill icon — all transfers on this date, both languages */}
                            <Box>
                                <IconButton
                                    size="small"
                                    onClick={() => openDrill({ dateRaw: row.LastConvDate })}
                                    sx={{ width: 24, height: 24 }}
                                    title="View transfer conversations for this date"
                                >
                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>

                            {COLS.map(col => {
                                // Figure out which language (if any) this cell belongs to,
                                // and whether the row has any of that language.
                                const isEng     = ENG_COLS.has(col.key);
                                const isSpa     = SPA_COLS.has(col.key);
                                const lang      = isEng ? "English" : isSpa ? "Spanish" : null;
                                const langCount = isEng ? engCount  : isSpa ? spaCount  : 0;
                                const clickable = !!lang && langCount > 0;
                                const onCellClick = clickable
                                    ? () => openDrill({ language: lang, dateRaw: row.LastConvDate })
                                    : undefined;

                                return (
                                    <Box key={col.key} sx={{
                                        display: "flex", alignItems: "center",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        ...(col.key === "LastConvDate" && { color: "text.secondary", whiteSpace: "nowrap" }),
                                        ...(col.key === "TotalRecords" && { fontWeight: 600, color: BLUE }),
                                    }}>
                                        {col.key === "LastConvDate" ? (
                                            formatDateISO(row.LastConvDate)
                                        ) : RATE_COLS.has(col.key) ? (
                                            <RatePill
                                                value={row[col.key]}
                                                isTransfer
                                                clickable={clickable}
                                                onClick={onCellClick}
                                            />
                                        ) : NO_RATE_COLS.has(col.key) ? (
                                            <RatePill
                                                value={row[col.key]}
                                                isTransfer={false}
                                                clickable={clickable}
                                                onClick={onCellClick}
                                            />
                                        ) : (col.key === "EnglishTotal" || col.key === "Spanish") ? (
                                            <Box
                                                onClick={onCellClick}
                                                sx={{
                                                    color: langCount > 0 ? BLUE : "text.disabled",
                                                    fontWeight: langCount > 0 ? 500 : 400,
                                                    cursor: clickable ? "pointer" : "default",
                                                    "&:hover": clickable ? { textDecoration: "underline" } : {},
                                                }}
                                            >
                                                {row[col.key] ?? "—"}
                                            </Box>
                                        ) : (
                                            row[col.key] ?? "—"
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                })}
            </Box>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
                <Box sx={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", mt: 1, px: 1,
                }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        Showing {page * ROWS_PER_PAGE + 1}–{Math.min((page + 1) * ROWS_PER_PAGE, rows.length)} of {rows.length}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 12, color: "text.secondary" }}>
                        <Box onClick={() => setPage(p => Math.max(0, p - 1))} sx={{
                            px: "10px", py: "3px", borderRadius: "20px",
                            border: "0.5px solid", borderColor: "divider",
                            cursor: page === 0 ? "default" : "pointer",
                            opacity: page === 0 ? 0.4 : 1,
                            "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                        }}>‹</Box>
                        <span>Page {page + 1} of {totalPages}</span>
                        <Box onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} sx={{
                            px: "10px", py: "3px", borderRadius: "20px",
                            border: "0.5px solid", borderColor: "divider",
                            cursor: page >= totalPages - 1 ? "default" : "pointer",
                            opacity: page >= totalPages - 1 ? 0.4 : 1,
                            "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                        }}>›</Box>
                    </Box>
                </Box>
            )}

            {/* Drill-down popup */}
            {drill && (
                <ConversationDrillDownDialog
                    open={!!drill}
                    onClose={() => setDrill(null)}
                    {...drill}
                />
            )}
        </>
    );
};

export default TransferTable;